import type { RouteLocationNormalized } from 'vue-router'
import { withoutTrailingSlash } from 'ufo'
import { isEnvFlagEnabled } from './envFlag'

const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay: () => void
      initializeCookiePolicyHtml?: () => void
    }
    dataLayer?: unknown[]
  }
}

/**
 * Whether the GTM container should load. Analytics is opt-in, and the config
 * default is a real boolean — but a `NUXT_PUBLIC_ANALYTICS_ENABLED` override
 * arrives as a string, so an environment that opts in reads `'true'`, not
 * `true`. Accept both shapes and treat anything else as off.
 */
export function isAnalyticsEnabled(
  value: unknown,
  gtmContainerId: string | undefined,
  onetrustDomainScript: string | undefined,
): boolean {
  return isEnvFlagEnabled(value)
    && GTM_CONTAINER_ID_PATTERN.test(gtmContainerId || '')
    && Boolean(onetrustDomainScript)
}

export function createGtmScript(gtmContainerId: string) {
  return {
    key: 'gtm-script',
    id: 'gtm-script',
    innerHTML: `
      (function(w,d,s,l,i){
        w[l]=w[l]||[];
        w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;
        j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmContainerId}');
    `,
  }
}

export function createOneTrustScript(onetrustDomainScript: string) {
  return {
    'key': 'onetrust-sdk',
    'src': 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
    'data-domain-script': onetrustDomainScript,
    'data-document-language': 'true',
  }
}

export function openCookiePreferences(): void {
  window.OneTrust?.ToggleInfoDisplay()
}

/** OneTrust fills its cookie list only at SDK load; hydrating the `v-html` body wipes it, so refill it per page. */
export function renderCookiePolicy(): void {
  const container = document.getElementById('ot-sdk-cookie-policy')
  if (!container || container.firstElementChild)
    return
  window.OneTrust?.initializeCookiePolicyHtml?.()
}

export interface VirtualPageViewEvent {
  event: 'virtual_page_view'
  page_path: string
  page_location: string
  page_title: string
}

/** A new page, not the initial navigation (no matched `from`) or a query, hash or trailing-slash change. */
export function isPageChange(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  return from.matched.length > 0 && withoutTrailingSlash(to.path) !== withoutTrailingSlash(from.path)
}

export function createVirtualPageView(location: Pick<Location, 'href' | 'pathname'>, title: string): VirtualPageViewEvent {
  return {
    event: 'virtual_page_view',
    page_path: location.pathname,
    page_location: location.href,
    page_title: title,
  }
}
